import type { FormField, ShowFormArtifact } from '@kanbu/schema/contracts';
import { Trans, useLingui } from '@lingui/react/macro';
import { ChevronDown } from 'lucide-react';
import { useState } from 'react';

import { cn } from '../../lib/cn';
import { Button } from '../../ui/button';

export interface FormArtifactViewProps {
  artifact: ShowFormArtifact;
  onSubmit?: (data: Record<string, unknown>) => void | Promise<void>;
  previewMode?: boolean;
  disabled?: boolean;
  className?: string;
  /** Notified when a submit attempt fails, so a host can offer a way out. */
  onSubmitFailed?: () => void;
}

const inputClassName =
  'flex h-10 w-full rounded-xl bg-chat-background px-3.5 text-sm text-chat-foreground shadow-sm ring-1 ring-chat-border/60 transition-shadow placeholder:text-muted-fg focus:outline-none focus:ring-2 focus:ring-chat-primary/40 disabled:opacity-60';

function fieldInputType(type: FormField['type']): string {
  switch (type) {
    case 'email':
      return 'email';
    case 'phone':
      return 'tel';
    case 'number':
      return 'number';
    case 'date':
      return 'date';

    default:
      return 'text';
  }
}

export function FormArtifactView({
  artifact,
  onSubmit,
  previewMode = false,
  disabled = false,
  className,
  onSubmitFailed,
}: FormArtifactViewProps) {
  const { t } = useLingui();
  const [values, setValues] = useState<Record<string, string>>(() =>
    Object.fromEntries(artifact.fields.map(field => [field.name, ''])),
  );
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [submitting, setSubmitting] = useState(false);
  const [submitFailed, setSubmitFailed] = useState(false);

  const setValue = (name: string, value: string) => {
    setValues(prev => ({ ...prev, [name]: value }));
    setErrors(prev => {
      const next = { ...prev };
      delete next[name];

      return next;
    });
  };

  const validate = (): Record<string, string> => {
    const nextErrors: Record<string, string> = {};

    for (const field of artifact.fields) {
      const value = values[field.name]?.trim() ?? '';

      if (field.required && !value) {
        nextErrors[field.name] = t`Required`;
        continue;
      }

      if (!value) {
        continue;
      }

      if (field.type === 'email' && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value)) {
        nextErrors[field.name] = t`Invalid email`;
        continue;
      }

      if (field.validation) {
        try {
          const regex = new RegExp(field.validation);

          if (!regex.test(value)) {
            nextErrors[field.name] = t`Invalid format`;
          }
        } catch {
          // ignore invalid admin regex at runtime
        }
      }
    }

    return nextErrors;
  };

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();

    const nextErrors = validate();
    setErrors(nextErrors);

    if (Object.keys(nextErrors).length > 0) {
      return;
    }

    const data = Object.fromEntries(
      artifact.fields.map(field => [field.name, values[field.name] ?? '']),
    );

    if (previewMode || !onSubmit) {
      return;
    }

    setSubmitting(true);
    setSubmitFailed(false);

    try {
      await onSubmit(data);
    } catch {
      /**
       * The values stay in the form so the user can retry without
       * retyping — a failed submit must never look like a successful one.
       */
      setSubmitFailed(true);
      onSubmitFailed?.();
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <form
      className={cn(
        'flex flex-col gap-5 rounded-2xl bg-chat-background-secondary p-5 shadow-md',
        className,
      )}
      onSubmit={handleSubmit}
    >
      <div className='space-y-1.5'>
        <h3 className='text-lg font-semibold text-chat-foreground'>
          {artifact.title}
        </h3>
        {artifact.description ? (
          <p className='text-sm leading-relaxed text-muted-fg'>
            {artifact.description}
          </p>
        ) : null}
      </div>

      <div className='flex flex-col gap-4'>
        {artifact.fields.map(field => {
          const error = errors[field.name];
          const fieldId = `form-field-${field.name}`;

          return (
            <div key={field.name} className='space-y-1.5'>
              <label
                htmlFor={fieldId}
                className='text-sm font-medium text-chat-foreground'
              >
                {field.label}
                {field.required ? (
                  <span className='text-danger'> *</span>
                ) : null}
              </label>

              {field.type === 'textarea' ? (
                <textarea
                  id={fieldId}
                  className={cn(inputClassName, 'min-h-24 py-2.5')}
                  value={values[field.name] ?? ''}
                  disabled={disabled || submitting}
                  placeholder={field.placeholder}
                  rows={3}
                  onChange={event => setValue(field.name, event.target.value)}
                />
              ) : field.type === 'select' ? (
                <div className='relative'>
                  <select
                    id={fieldId}
                    className={cn(inputClassName, 'peer appearance-none pr-10')}
                    value={values[field.name] ?? ''}
                    disabled={disabled || submitting}
                    onChange={event => setValue(field.name, event.target.value)}
                  >
                    <option value=''>{t`Select…`}</option>
                    {(field.options ?? []).map(option => (
                      <option key={option} value={option}>
                        {option}
                      </option>
                    ))}
                  </select>
                  <ChevronDown
                    aria-hidden='true'
                    className='pointer-events-none absolute right-3.5 top-1/2 size-4 -translate-y-1/2 text-chat-foreground peer-disabled:opacity-60'
                  />
                </div>
              ) : (
                <input
                  id={fieldId}
                  className={inputClassName}
                  type={fieldInputType(field.type)}
                  value={values[field.name] ?? ''}
                  disabled={disabled || submitting}
                  placeholder={field.placeholder}
                  onChange={event => setValue(field.name, event.target.value)}
                />
              )}

              {error ? <p className='text-xs text-danger'>{error}</p> : null}
            </div>
          );
        })}
      </div>

      {submitFailed ? (
        <p className='text-xs text-danger'>
          <Trans>
            Could not send the form. Check your connection and try again.
          </Trans>
        </p>
      ) : null}

      <Button
        type='submit'
        disabled={disabled || submitting || previewMode}
        loading={submitting}
        className='w-full rounded-full'
      >
        {artifact.submitLabel || t`Submit`}
      </Button>

      {previewMode ? (
        <p className='text-xs text-muted-fg'>
          <Trans>Preview only — submit is disabled in the editor.</Trans>
        </p>
      ) : null}
    </form>
  );
}
